import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type {
  ConditionStatus,
  PatientCondition,
} from '@/types/patient-chart-api';

/**
 * Patient conditions list — Chart > Problems tab (PROD-736 PR B).
 *
 * Wire (Flutter `patient_chart_data_source_impl.dart:509`):
 *
 *   GET /api/Patients/GetConditionStatusDetails?status={Active|Resolved}&PatientId={id}
 *       → PatientCondition[]
 *
 * The endpoint partitions by `clinicalStatus` — Flutter fires it twice
 * (once for "Active", once for "Resolved") and renders the two lists as
 * separate sections of the Problems tab. We do the same via two parallel
 * `useQuery` calls.
 *
 * Cache: 60s staleTime — conditions change during a visit (mark resolved)
 * but not so often that we want to refetch on every focus.
 */

export function usePatientConditions(
  patientId: string | null | undefined,
  status: ConditionStatus,
) {
  return useQuery({
    queryKey: ['patient-conditions', patientId, status],
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams({
        status,
        PatientId: patientId ?? '',
      });
      return get<PatientCondition[]>(
        `/api/Patients/GetConditionStatusDetails?${qs.toString()}`,
        { signal },
      );
    },
    enabled: !!patientId,
    staleTime: 60_000,
  });
}
